import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';

const HEADING_SELECTOR = 'h1, h2, h3';
const ID_PREFIX = 'doc-outline-';
/** 滚动定位时给吸顶导航留的高度 */
const SCROLL_OFFSET = 84;

const cleanText = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();

/**
 * 从已渲染的正文里收集标题，生成目录数据。
 * 不依赖正文是 Markdown 还是现成 HTML：渲染完后直接读 DOM 里的 h1 / h2 / h3，给没有 id 的标题补上锚点。
 *
 * @param {import('react').RefObject<HTMLElement>} containerRef 正文容器（只收集它里面的标题）
 * @param {any[]} deps 正文变化的依赖（内容字符串等），变了就重新收集
 * @returns {{ items: {id:string,text:string,depth:number}[], activeId: string, scrollTo: (id:string)=>void }}
 */
export function useDocOutline(containerRef, deps) {
    const [items, setItems] = useState([]);
    const [activeId, setActiveId] = useState('');

    useLayoutEffect(() => {
        const root = containerRef.current;
        const nodes = root
            ? Array.from(root.querySelectorAll(HEADING_SELECTOR)).filter((el) => cleanText(el))
            : [];
        if (!nodes.length) {
            setItems([]);
            return;
        }
        // 层级按文中出现的最高级别折算：最高级是章，其次是节，再往下是小节
        const levels = nodes.map((el) => Number(el.tagName.slice(1)));
        const top = Math.min(...levels);
        const next = nodes.map((el, i) => {
            if (!el.id) el.id = `${ID_PREFIX}${i + 1}`;
            return { id: el.id, text: cleanText(el), depth: Math.min(levels[i] - top, 2) };
        });
        setItems(next);
        setActiveId((prev) => (next.some((item) => item.id === prev) ? prev : next[0].id));
    }, deps);

    // 滚动时高亮当前所在的标题（最后一个滚过视口上沿的标题）
    useEffect(() => {
        if (!items.length || typeof window === 'undefined') return undefined;
        const onScroll = () => {
            let current = items[0].id;
            for (const item of items) {
                const el = document.getElementById(item.id);
                if (!el) continue;
                if (el.getBoundingClientRect().top - SCROLL_OFFSET - 12 <= 0) current = item.id;
                else break;
            }
            setActiveId(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [items]);

    const scrollTo = useCallback((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        setActiveId(id);
        const top = el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
        try {
            window.scrollTo({ top, behavior: 'smooth' });
        } catch {
            window.scrollTo(0, top);
        }
    }, []);

    return useMemo(() => ({ items, activeId, scrollTo }), [items, activeId, scrollTo]);
}

/**
 * 只读预览 / 范文阅读页左侧的目录：章节列表 + 当前位置高亮，点击滚到对应标题。
 */
export default function DocOutline({
    items,
    activeId,
    onSelect,
    title = '目录',
    emptyText = '正文里还没有章节标题',
}) {
    const chapterCount = items.filter((item) => item.depth === 0).length;
    return (
        <nav className={styles.outline} aria-label={title}>
            <div className={styles.head}>
                <span className={styles.headTitle}>{title}</span>
                {chapterCount > 0 ? <span className={styles.count}>{chapterCount} 章</span> : null}
            </div>
            {items.length ? (
                <ul className={styles.list}>
                    {items.map((item) => (
                        <li key={item.id}>
                            <button
                                type="button"
                                className={styles.item}
                                data-depth={item.depth}
                                aria-current={item.id === activeId ? 'location' : undefined}
                                title={item.text}
                                onClick={() => onSelect?.(item.id)}
                            >
                                <span className={styles.mark} />
                                <span className={styles.label}>{item.text}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className={styles.empty}>{emptyText}</div>
            )}
        </nav>
    );
}
